import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import { track } from "../lib/analytics";

const PROJECT_TYPES = [
  "Brand identity",
  "Film / Video",
  "Editorial",
  "Digital",
  "Art direction",
  "Campaign",
  "Other",
];

export default function Contact({ settings }) {
  const [contact, setContact] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", project_type: "", message: "" });
  const firstFieldRef = useRef(null);

  useEffect(() => {
    api.get("/contact").then(({ data }) => setContact(data));
  }, []);

  // autofocus first field once loaded
  useEffect(() => {
    if (contact && firstFieldRef.current) firstFieldRef.current.focus();
  }, [contact]);

  if (!contact) {
    return (
      <main className="min-h-[80vh] flex items-center justify-center">
        <p className="overline text-[#8A8A93]">Loading…</p>
      </main>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    track("contact_form_submit_attempted", {
      has_company: Boolean(form.company),
      has_project_type: Boolean(form.project_type),
      project_type: form.project_type || "unspecified",
      message_length: form.message.length,
    });
    try {
      await api.post("/inquiries", form);
      setSubmitted(true);
      track("contact_form_submitted", {
        project_type: form.project_type || "unspecified",
        has_company: Boolean(form.company),
        message_length: form.message.length,
      });
      setForm({ name: "", email: "", company: "", project_type: "", message: "" });
    } catch (err) {
      track("contact_form_submit_failed", {
        project_type: form.project_type || "unspecified",
        error: err?.response?.status || "network",
      });
    } finally {
      setSending(false);
    }
  };

  // Opening accent split (last word in gold italic if `accent` empty)
  const heading = (contact.opening_line || "").trim();
  const accent = (contact.opening_accent || "").trim();
  const headingParts = (() => {
    if (!heading) return null;
    if (accent && heading.toLowerCase().includes(accent.toLowerCase())) {
      const i = heading.toLowerCase().lastIndexOf(accent.toLowerCase());
      return { before: heading.slice(0, i), accent: heading.slice(i, i + accent.length), after: heading.slice(i + accent.length) };
    }
    const words = heading.split(/\s+/);
    if (words.length < 2) return { before: "", accent: heading, after: "" };
    const last = words[words.length - 1];
    return { before: words.slice(0, -1).join(" ") + " ", accent: last.replace(/\.$/, ""), after: last.endsWith(".") ? "." : "" };
  })();

  const email = settings?.contact_email;
  const phone = settings?.contact_phone;
  const address = (contact.location_body && contact.location_body.trim()) || settings?.contact_address || "";
  const wa = settings?.whatsapp_number;
  const waMsg = settings?.whatsapp_message || "Hi, I'd like to discuss a project.";
  const insta = settings?.instagram_url;
  const linkedin = settings?.linkedin_url;
  const twitter = settings?.twitter_url;

  return (
    <main data-testid="contact-page" className="bg-[#0A0A0C] overflow-x-hidden">
      {/* Section 1 — Opening */}
      {contact.opening_visible && headingParts && (
        <section data-testid="contact-opening" className="px-6 md:px-14 lg:px-24 pt-40 md:pt-56 pb-24 md:pb-32 border-b border-white/5">
          <Reveal>
            <p className="overline mb-10 md:mb-16">{contact.opening_overline || "Contact"}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-[2.25rem] sm:text-5xl md:text-7xl lg:text-[7rem] leading-[0.95] tracking-[-0.04em] max-w-[16ch] break-words">
              {headingParts.before}
              <span className="font-serif-accent text-[#C9A050]">{headingParts.accent}</span>
              {headingParts.after}
            </h1>
          </Reveal>
        </section>
      )}

      {/* Split layout — info left, form right */}
      <section className="px-6 md:px-14 lg:px-24 py-24 md:py-40 border-b border-white/5">
        <div className="grid grid-cols-12 gap-12 md:gap-16">
          {/* LEFT — info blocks */}
          <aside className="col-span-12 md:col-span-5 space-y-14 md:space-y-20">
            {/* Direct contact */}
            {contact.direct_visible && (
              <Reveal>
                <div data-testid="contact-direct">
                  <p className="overline text-[#8A8A93] mb-5">{contact.direct_label || "Direct"}</p>
                  <ul className="space-y-3">
                    {email && (
                      <li>
                        <a href={`mailto:${email}`} data-testid="contact-email-link" data-cursor="Mail" className="text-xl md:text-2xl font-light text-[#E4E4E7] hover:text-[#C9A050] transition-colors">{email}</a>
                      </li>
                    )}
                    {phone && (
                      <li>
                        <a href={`tel:${phone.replace(/\s+/g,"")}`} data-cursor="Call" className="text-base md:text-lg font-light text-[#A1A1AA] hover:text-[#C9A050] transition-colors">{phone}</a>
                      </li>
                    )}
                    {wa && (
                      <li>
                        <a href={`https://wa.me/${wa}?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noopener noreferrer" data-cursor="Reach" className="text-base md:text-lg font-light text-[#A1A1AA] hover:text-[#C9A050] transition-colors">WhatsApp</a>
                      </li>
                    )}
                  </ul>
                </div>
              </Reveal>
            )}

            {contact.location_visible && (address || contact.location_map_embed) && (
              <Reveal delay={0.05}>
                <div data-testid="contact-location">
                  <p className="overline text-[#8A8A93] mb-5">{contact.location_label || "Studio"}</p>
                  {address && (
                    <p className="text-base md:text-lg text-[#A1A1AA] leading-relaxed whitespace-pre-line max-w-sm">{address}</p>
                  )}
                  {contact.location_map_embed && (
                    <MapPreview
                      src={extractMapSrc(contact.location_map_embed)}
                      label={contact.location_label || "Studio"}
                      address={address}
                    />
                  )}
                </div>
              </Reveal>
            )}

            {contact.social_visible && (insta || linkedin || twitter) && (
              <Reveal delay={0.1}>
                <div data-testid="contact-social">
                  <p className="overline text-[#8A8A93] mb-5">{contact.social_label || "Elsewhere"}</p>
                  <ul className="space-y-2">
                    {insta && <li><a href={insta} target="_blank" rel="noopener noreferrer" className="overline hover:text-[#C9A050] transition-colors">Instagram →</a></li>}
                    {linkedin && <li><a href={linkedin} target="_blank" rel="noopener noreferrer" className="overline hover:text-[#C9A050] transition-colors">LinkedIn →</a></li>}
                    {twitter && <li><a href={twitter} target="_blank" rel="noopener noreferrer" className="overline hover:text-[#C9A050] transition-colors">Twitter →</a></li>}
                  </ul>
                </div>
              </Reveal>
            )}
          </aside>

          {/* RIGHT — form */}
          <div className="col-span-12 md:col-span-7">
            {contact.form_visible && (
              <div data-testid="contact-form-block">
                <Reveal>
                  <p className="overline text-[#8A8A93] mb-8">{contact.form_label || "Tell us about your project"}</p>
                </Reveal>
                <AnimatePresence mode="wait" initial={false}>
                  {submitted ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      data-testid="contact-success"
                      className="border border-[#C9A050]/30 bg-[#C9A050]/[0.03] px-8 py-12"
                    >
                      <p className="font-display text-3xl md:text-4xl tracking-tight mb-4">
                        Got it<span className="text-[#C9A050]">.</span>
                      </p>
                      <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed whitespace-pre-line">
                        {contact.success_body || "We'll get back to you within 48 hours."}
                      </p>
                      <button
                        type="button"
                        onClick={() => setSubmitted(false)}
                        data-testid="contact-success-reset"
                        className="overline mt-10 text-[#C9A050] hover:underline"
                      >
                        Send another →
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      onSubmit={submit}
                      data-testid="contact-form"
                      className="space-y-7"
                      noValidate
                    >
                      <FormField label="Your name" required>
                        <input
                          ref={firstFieldRef}
                          type="text"
                          value={form.name}
                          onChange={(e) => set("name", e.target.value)}
                          placeholder="Karthick"
                          required
                          data-testid="cf-name"
                          className={fieldCls}
                        />
                      </FormField>
                      <FormField label="Email" required>
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => set("email", e.target.value)}
                          placeholder="hello@yourdomain.com"
                          required
                          data-testid="cf-email"
                          className={fieldCls}
                        />
                      </FormField>
                      <FormField label="Company">
                        <input
                          type="text"
                          value={form.company}
                          onChange={(e) => set("company", e.target.value)}
                          placeholder="Studio or brand name"
                          data-testid="cf-company"
                          className={fieldCls}
                        />
                      </FormField>
                      <FormField label="Project type">
                        <select
                          value={form.project_type}
                          onChange={(e) => set("project_type", e.target.value)}
                          data-testid="cf-project-type"
                          className={fieldCls}
                        >
                          <option value="">Select an option</option>
                          {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </FormField>
                      <FormField label="Message" required>
                        <textarea
                          rows={5}
                          value={form.message}
                          onChange={(e) => set("message", e.target.value)}
                          placeholder="Tell us about your project…"
                          required
                          data-testid="cf-message"
                          className={fieldCls}
                        />
                      </FormField>
                      <div className="pt-2">
                        <button
                          type="submit"
                          disabled={sending}
                          data-testid="cf-submit"
                          data-cursor="Send"
                          className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-8 py-4 transition-all disabled:opacity-50"
                        >
                          {sending ? "Sending…" : "Send →"}
                        </button>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Closing line */}
      {contact.closing_visible && contact.closing_body && (
        <section data-testid="contact-closing" className="px-6 md:px-14 lg:px-24 py-32 md:py-56">
          <div className="grid grid-cols-12">
            <Reveal className="col-span-12 md:col-span-10 md:col-start-2">
              <p className="font-serif-accent text-[#C9A050] text-2xl md:text-3xl lg:text-4xl leading-[1.4] font-light max-w-3xl whitespace-pre-line">
                {contact.closing_body}
              </p>
            </Reveal>
          </div>
        </section>
      )}
    </main>
  );
}

/* Accept either a Google Maps embed URL or a full iframe HTML — return only the src URL. */
function extractMapSrc(value) {
  const v = (value || "").trim();
  if (!v) return "";
  const match = v.match(/src=["']([^"']+)["']/i);
  return match ? match[1] : v;
}

/**
 * MapPreview
 * Compact thumbnail (max-w-sm, aspect 4:3) sized to roughly match the email
 * line above it. Clicking opens a fullscreen modal with the full map and
 * "Open in Google Maps" link.
 *
 * Trade-off: the small iframe still renders the live Google embed (which is
 * a real ~25KB iframe). To keep mobile fast we lazy-load it and serve the
 * heavy interactive view only inside the modal.
 */
function MapPreview({ src, label, address }) {
  const [open, setOpen] = useState(false);

  // Body scroll lock while modal is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="contact-location-map"
        aria-label="Open studio map"
        className="mt-6 relative overflow-hidden rounded-md w-full max-w-[16rem] md:max-w-sm aspect-[4/3] bg-[#0A0A0C] group block text-left cursor-pointer"
      >
        {/* offset gold matted-frame plate */}
        <span
          aria-hidden="true"
          className="absolute -inset-px translate-x-1.5 translate-y-1.5 border border-[#C9A050]/55 rounded-md transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-0 group-hover:translate-y-0 group-hover:border-[#C9A050]"
        />
        <div className="relative w-full h-full overflow-hidden rounded-md border border-[#C9A050]/35">
          <iframe
            src={src}
            title={label}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ border: 0, filter: "grayscale(0.85) contrast(1.08) brightness(0.7) saturate(0.85)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            tabIndex={-1}
            aria-hidden="true"
          />
          {/* gold pin + pulse */}
          <div aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full pointer-events-none">
            <div className="relative">
              <span
                className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-[#C9A050]/30"
                style={{ animation: "ap-map-ping 2.4s cubic-bezier(0,0,0.2,1) infinite" }}
              />
              <svg width="18" height="22" viewBox="0 0 22 28" fill="none" className="relative">
                <path d="M11 27 C11 27, 1 17 1 11 A10 10 0 1 1 21 11 C21 17, 11 27, 11 27 Z" fill="#C9A050" stroke="#0A0A0C" strokeWidth="1.2" />
                <circle cx="11" cy="11" r="3.2" fill="#0A0A0C" />
              </svg>
            </div>
          </div>
          {/* corner: label + address line */}
          <div className="absolute bottom-2 left-2 max-w-[70%] pointer-events-none">
            <p className="overline text-[9px] tracking-[0.28em] text-[#C9A050] mb-0.5">{label}</p>
            {address && (
              <p className="font-serif-accent italic text-white/85 text-[11px] leading-tight whitespace-pre-line drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]">
                {address.split("\n")[0]}
              </p>
            )}
          </div>
          {/* top-right "expand" chip on hover */}
          <div className="absolute top-2 right-2 px-2 py-1 rounded-sm bg-black/55 border border-[#C9A050]/40 text-[#C9A050] text-[9px] tracking-[0.25em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            Expand
          </div>
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent 45%)" }} />
        </div>
        <style>{`
          @keyframes ap-map-ping {
            0%   { transform: translateX(-50%) scale(0.5); opacity: 0.85; }
            80%  { transform: translateX(-50%) scale(2.3); opacity: 0; }
            100% { transform: translateX(-50%) scale(2.3); opacity: 0; }
          }
        `}</style>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="contact-location-map-modal"
            className="fixed inset-0 z-[150] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-5xl aspect-[16/10] bg-[#0A0A0C] rounded-md overflow-hidden border border-[#C9A050]/40 shadow-[0_24px_72px_-12px_rgba(0,0,0,0.8)]"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={src}
                title={`${label} — large`}
                className="absolute inset-0 w-full h-full"
                style={{ border: 0, filter: "grayscale(0.6) contrast(1.05) brightness(0.85)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close map"
                data-testid="contact-location-map-close"
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 hover:bg-[#C9A050] hover:text-black text-white flex items-center justify-center transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              {(address || label) && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((address || label).replace(/\n+/g, ", "))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-location-map-open"
                  className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#C9A050] text-black text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-white transition-colors shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)]"
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                    <path d="M6.5 0C3.5 0 1 2.5 1 5.5C1 9.5 6.5 13 6.5 13C6.5 13 12 9.5 12 5.5C12 2.5 9.5 0 6.5 0Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" fill="none" />
                    <circle cx="6.5" cy="5.5" r="1.8" fill="currentColor" />
                  </svg>
                  Open in Google Maps
                </a>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

const fieldCls =
  "w-full bg-transparent border-b border-white/15 focus:border-[#C9A050] hover:border-white/35 outline-none px-0 py-3 text-base md:text-lg text-white placeholder:text-[#5A5A60] transition-colors duration-300";

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline text-[#8A8A93] block mb-3">
        {label}{required && <span className="text-[#C9A050]"> *</span>}
      </span>
      {children}
    </label>
  );
}
